import { Button, Divider, Paper, Stack, Typography, IconButton } from '@mui/material'
import { Fragment, useMemo } from 'react'
import { InfoOutlined } from '@mui/icons-material'
import ArrowTooltips from '@common/components/Tooltip'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'

import Exam from './Exam'
import { QcTaskTimepointSchema } from '@common/config/api/client'
import { SPACING } from '@common/theme/spacing'
import { launchDicomViewerForQC } from '@common/utils/ohifUtils'
import { useParams } from 'react-router-dom'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'

export default function Exams({ qcTask }: { qcTask: QcTaskTimepointSchema }) {
  const sortedSubmissions = useMemo(() => {
    return [...qcTask.timepoint_container_submission].sort((a, b) =>
      a.container_requirement_id.localeCompare(b.container_requirement_id),
    )
  }, [qcTask.timepoint_container_submission])

  const { qcTaskId, studyId } = useParams<{ qcTaskId: string; studyId: string }>()
  const { user } = useExtendedAuth()

  // Check if we have a series UID to use as the initial view
  const firstDeriesInstanceId =
    qcTask.timepoint_container_submission?.[0]?.exam_metadata?.[0]?.series?.[0]?.series_uid || undefined

  if (!qcTaskId || !studyId || !user?.access_token) {
    toast(
      <Toast
        message="Missing required parameters for QC task viewer. Please ensure study ID, QC task ID, and authentication token are provided."
        variant="error"
      />,
    )
    return false
  }

  return (
    <Paper
      sx={{
        padding: SPACING.spacingXl,
        borderRadius: SPACING.borderRadiusXxl,
      }}
    >
      <Stack spacing={SPACING.spacingXxl}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Exams</Typography>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              sx={{
                color: 'text.primary',
                borderColor: 'secondary.dark',
                py: SPACING.spacingMd,
                px: SPACING.spacingLg,
              }}
              variant="outlined"
              onClick={() => {
                launchDicomViewerForQC(studyId!, qcTaskId!, user!.access_token, firstDeriesInstanceId)
              }}
            >
              Open in viewer
            </Button>
            {/* <Button
              sx={{
                color: 'text.primary',
                borderColor: 'secondary.dark',
                py: SPACING.spacingMd,
                px: SPACING.spacingLg,
              }}
              variant="outlined"
              onClick={() => {
                launchDicomViewerForSegmentation(studyId!, qcTaskId!, user!.access_token, firstDeriesInstanceId)
              }}
            >
              Open in segmentation Mode
            </Button> */}
          </div>
        </Stack>

        <Stack spacing={SPACING.spacingXl}>
          {sortedSubmissions.map((container, index) => {
            const prevContainer = index > 0 ? sortedSubmissions[index - 1] : undefined

            const isNewRequirement =
              !prevContainer || prevContainer.container_requirement_id !== container.container_requirement_id

            const siteName = qcTask.sites.find((site) => site.site_pseudo_id === container.site_pseudo_id)?.site_name

            return (
              <Fragment key={container.container_submission_id}>
                {index !== 0 && <Divider />}
                {isNewRequirement && (
                  <Stack direction="row" spacing={SPACING.spacingSm} alignItems="center" justifyContent="start">
                    <Typography variant="subtitle1">{siteName}</Typography>
                    <ArrowTooltips message={`Site ID: ${container.site_pseudo_id}`} placement="top">
                      <IconButton sx={{ padding: SPACING.noSpacing }}>
                        <InfoOutlined sx={{ color: 'text.secondary', width: 16, height: 16 }} />
                      </IconButton>
                    </ArrowTooltips>
                  </Stack>
                )}
                <Exam
                  qcTaskOrder={qcTask.order}
                  qcTaskStatus={qcTask.task_state}
                  timepointSubmissionId={qcTask.timepoint_submission.timepoint_submission_id}
                  container={container}
                />
              </Fragment>
            )
          })}
        </Stack>
      </Stack>
    </Paper>
  )
}
